import { Component, OnInit, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { LeaveService, LeaveRequest, LeaveStatus, ReviewPayload } from '../../../services/leave.service';
import { LanguageService } from '../../core/services/language.service';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';

@Component({
  selector: 'app-leave-management',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './leave-management.component.html',
  styleUrls: ['./leave-management.component.css']
})
export class LeaveManagementComponent implements OnInit {
  leaves: LeaveRequest[] = [];
  isLoading = false;
  error: string | null = null;

  // Pagination
  currentPage = 1;
  pageSize = 10;
  totalPages = 1;
  totalElements = 0;

  // Filtre
  selectedStatus: LeaveStatus | '' = '';

  // Modale review (saisie commentaire)
  showReviewModal = false;
  reviewDecision: 'APPROVED' | 'REJECTED' = 'APPROVED';
  selectedLeave: LeaveRequest | null = null;
  managerComment = '';
  isActioning = false;
  reviewError: string | null = null;

  // Popup de confirmation finale (étape 2)
  showReviewConfirm = false;

  propertyId: number | null = null;

  constructor(
    private leaveService: LeaveService,
    private languageService: LanguageService,
    private route: ActivatedRoute,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  t(key: string): string {
    return this.languageService.translate(key);
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.propertyId = +id;
      this.loadLeaves();
    } else {
      this.error = 'ID de projet non trouvé dans l\'URL.';
    }
  }

  loadLeaves(): void {
    if (!this.propertyId) return;
    this.isLoading = true;
    this.error = null;

    const page = this.currentPage - 1;
    const status = this.selectedStatus || undefined;

    this.leaveService.getByProperty(this.propertyId, page, this.pageSize, status).pipe(
      catchError(err => {
        this.error = err.error?.message || 'Erreur lors du chargement des congés.';
        this.isLoading = false;
        return of({ content: [], totalElements: 0, totalPages: 1, number: 0, size: this.pageSize, first: true, last: true });
      })
    ).subscribe(res => {
      this.leaves = res.content;
      this.totalElements = res.totalElements;
      this.totalPages = res.totalPages || 1;
      this.isLoading = false;
    });
  }

  onStatusFilterChange(): void {
    this.currentPage = 1;
    this.loadLeaves();
  }

  // ── Review (Approuver / Refuser) ───────────────────────────────────────────

  openReviewModal(leave: LeaveRequest, decision: 'APPROVED' | 'REJECTED'): void {
    this.selectedLeave = leave;
    this.reviewDecision = decision;
    this.managerComment = '';
    this.reviewError = null;
    this.showReviewModal = true;
    this.lockScroll();
  }

  closeReviewModal(): void {
    this.showReviewModal = false;
    this.selectedLeave = null;
    this.reviewError = null;
    this.unlockScroll();
  }

  onReviewBackdrop(event: Event): void {
    if (event.target === event.currentTarget) this.closeReviewModal();
  }

  // Étape 1 : valide le formulaire puis ouvre la confirmation finale
  reviewLeaveRequest(): void {
    if (!this.selectedLeave || !this.propertyId) return;

    if (this.reviewDecision === 'REJECTED' && !this.managerComment.trim()) {
      this.reviewError = 'Un motif est requis pour refuser un congé.';
      return;
    }

    this.reviewError = null;
    this.showReviewConfirm = true;
  }

  cancelReviewConfirm(): void {
    this.showReviewConfirm = false;
  }

  // Étape 2 : confirmation finale → appel API
  confirmReviewFinal(): void {
    if (!this.selectedLeave || !this.propertyId) return;

    this.showReviewConfirm = false;
    this.isActioning = true;

    const payload: ReviewPayload = {
      decision: this.reviewDecision,
      managerComment: this.managerComment.trim()
    };

    this.leaveService.review(this.propertyId, this.selectedLeave.id, payload).subscribe({
      next: () => {
        this.isActioning = false;
        this.closeReviewModal();
        this.loadLeaves();
      },
      error: err => {
        this.reviewError = err.error?.message || 'Erreur lors du traitement.';
        this.isActioning = false;
      }
    });
  }

  // ── Utilitaires UI ─────────────────────────────────────────────────────────

  getStatusBadgeClass(status: LeaveStatus): string {
    switch (status) {
      case 'APPROVED': return 'bg-green-100 text-green-800 border border-green-200';
      case 'REJECTED': return 'bg-red-100 text-red-800 border border-red-200';
      default:         return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
    }
  }

  getStatusDotClass(status: LeaveStatus): string {
    switch (status) {
      case 'APPROVED': return 'bg-green-500';
      case 'REJECTED': return 'bg-red-500';
      default:         return 'bg-yellow-500';
    }
  }

  getStatusLabel(status: LeaveStatus): string {
    switch (status) {
      case 'APPROVED': return 'Approuvé';
      case 'REJECTED': return 'Refusé';
      default:         return 'En attente';
    }
  }

  formatDate(arr: number[]): string {
    return this.leaveService.formatDateArray(arr);
  }

  getWorkerName(leave: LeaveRequest): string {
    return `${leave.workerFirstName} ${leave.workerLastName}`;
  }

  countByStatus(status: LeaveStatus): number {
    return this.leaves.filter(l => l.status === status).length;
  }

  // ── Pagination ─────────────────────────────────────────────────────────────

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page;
      this.loadLeaves();
    }
  }
  nextPage(): void { if (this.currentPage < this.totalPages) { this.currentPage++; this.loadLeaves(); } }
  previousPage(): void { if (this.currentPage > 1) { this.currentPage--; this.loadLeaves(); } }
  hasNextPage(): boolean { return this.currentPage < this.totalPages; }
  hasPreviousPage(): boolean { return this.currentPage > 1; }

  getPaginationText(): string {
    const start = (this.currentPage - 1) * this.pageSize + 1;
    const end = Math.min(this.currentPage * this.pageSize, this.totalElements);
    return `Voir ${start}-${end} sur ${this.totalElements}`;
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const max = 5;
    if (this.totalPages <= max) {
      for (let i = 1; i <= this.totalPages; i++) pages.push(i);
    } else {
      const half = Math.floor(max / 2);
      let start = Math.max(1, this.currentPage - half);
      let end = Math.min(this.totalPages, start + max - 1);
      if (end - start < max - 1) start = Math.max(1, end - max + 1);
      for (let i = start; i <= end; i++) pages.push(i);
    }
    return pages;
  }

  private lockScroll(): void {
    if (isPlatformBrowser(this.platformId)) document.body.style.overflow = 'hidden';
  }

  private unlockScroll(): void {
    if (isPlatformBrowser(this.platformId)) document.body.style.overflow = '';
  }
}
